import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getAllRaffles } from "@/services/raffleService";
import type { VehicleRaffle, RaffleStatus } from "@/types/raffle";
import { queryKeys } from "@/lib/queryKeys";
import { Plus, Ticket, Pencil, Eye, Car, Gift } from "lucide-react";

const STATUS_LABELS: Record<RaffleStatus, string> = {
  upcoming: "Próxima",
  active: "Activa",
  drawing: "En sorteo",
  completed: "Finalizada",
  cancelled: "Cancelada",
};

const STATUS_VARIANT: Record<RaffleStatus, "default" | "secondary" | "destructive" | "outline"> = {
  upcoming: "secondary",
  active: "default",
  drawing: "outline",
  completed: "secondary",
  cancelled: "destructive",
};

function formatDate(value: VehicleRaffle["drawDate"]): string {
  if (value == null) return "—";
  const d = value instanceof Date ? value : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function" ? (value as { toDate: () => Date }).toDate() : "seconds" in value ? new Date((value as { seconds: number }).seconds * 1000) : null;
  return d ? d.toLocaleDateString("es-DO", { day: "numeric", month: "short", year: "numeric" }) : "—";
}

function soldCount(raffle: VehicleRaffle): number {
  const sel = raffle.selectedNumbers;
  if (sel && typeof sel === "object") return Object.keys(sel).length;
  const total = raffle.totalNumbers ?? 0;
  const avail = raffle.availableNumbers?.length ?? 0;
  return total - avail;
}

export function Raffles() {
  const [statusFilter, setStatusFilter] = useState<RaffleStatus | "all">("all");
  const { data: raffles = [], isLoading } = useQuery({
    queryKey: queryKeys.raffles,
    queryFn: getAllRaffles,
  });

  const filtered = useMemo(() => {
    if (statusFilter === "all") return raffles;
    return raffles.filter((r) => r.status === statusFilter);
  }, [raffles, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Rifas</h1>
          <p className="text-muted-foreground">Gestionar rifas de la app móvil</p>
        </div>
        <Button asChild>
          <Link to="/raffles/new" className="gap-2">
            <Plus className="h-4 w-4" />
            Nueva rifa
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["all", "upcoming", "active", "completed"] as const).map((f) => (
          <Button
            key={f}
            variant={statusFilter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(f)}
          >
            {f === "all" ? "Todas" : f === "upcoming" ? "Próximas" : f === "active" ? "Activas" : "Finalizadas"}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <p className="text-muted-foreground">Cargando rifas…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[56px]">Imagen</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Premio</TableHead>
                  <TableHead>Números</TableHead>
                  <TableHead>Participantes</TableHead>
                  <TableHead>Sorteo</TableHead>
                  <TableHead className="w-[120px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const sold = soldCount(r);
                  const prizeName = r.prizeType === "vehicle" ? r.vehicleName : r.customItem?.name;
                  const img = r.prizeType === "vehicle" ? r.vehicleImage : r.customItem?.image;
                  return (
                    <TableRow key={r.id}>
                      <TableCell>
                        {img ? (
                          <img src={img} alt="" className="h-12 w-12 rounded object-cover border border-border" />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-muted-foreground/30 bg-muted/50 text-muted-foreground">
                            {r.prizeType === "vehicle" ? <Car className="h-6 w-6" /> : <Gift className="h-6 w-6" />}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{r.title}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                      </TableCell>
                      <TableCell>{prizeName ?? "—"}</TableCell>
                      <TableCell>
                        {sold} / {r.totalNumbers}
                      </TableCell>
                      <TableCell>{r.totalParticipants}</TableCell>
                      <TableCell>{formatDate(r.drawDate)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" asChild title="Ver detalle">
                            <Link to={`/raffles/${r.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button variant="ghost" size="icon" asChild title="Editar">
                            <Link to={`/raffles/edit/${r.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Ticket className="h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-muted-foreground">No hay rifas {statusFilter !== "all" && STATUS_LABELS[statusFilter as RaffleStatus].toLowerCase()}</p>
              <Button asChild className="mt-4">
                <Link to="/raffles/new" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Crear primera rifa
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
